import { useCallback, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import type { KnowledgeBaseCategory, KnowledgeBaseCategoryAssignment } from '@/types/supabase';

const CATEGORY_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#14b8a6'];

export function useKnowledgeBaseCategories() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [categories, setCategories] = useState<KnowledgeBaseCategory[]>([]);
  const [assignments, setAssignments] = useState<KnowledgeBaseCategoryAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchKnowledgeBaseCategories = useCallback(async () => {
    if (!user) {
      setCategories([]);
      setAssignments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const [{ data: categoryRows, error: categoryError }, { data: assignmentRows, error: assignmentError }] = await Promise.all([
        supabase
          .from('knowledge_base_categories')
          .select('*')
          .eq('user_id', user.id)
          .order('position', { ascending: true })
          .order('created_at', { ascending: true }),
        supabase
          .from('knowledge_base_category_assignments')
          .select('*')
          .eq('user_id', user.id),
      ]);

      if (categoryError) throw categoryError;
      if (assignmentError) throw assignmentError;

      setCategories((categoryRows ?? []) as KnowledgeBaseCategory[]);
      setAssignments((assignmentRows ?? []) as KnowledgeBaseCategoryAssignment[]);
    } catch (error) {
      console.error('Error fetching knowledge base categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to load knowledge base categories',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, user]);

  const createCategory = useCallback(async (name: string) => {
    if (!user) return null;

    try {
      const nextPosition = categories.length;
      const color = CATEGORY_COLORS[nextPosition % CATEGORY_COLORS.length];

      const { data, error } = await (supabase as any)
        .from('knowledge_base_categories')
        .insert({
          user_id: user.id,
          name,
          color,
          position: nextPosition,
        })
        .select('*')
        .single();

      if (error) throw error;

      const category = data as KnowledgeBaseCategory;
      setCategories((current) => [...current, category]);
      return category;
    } catch (error) {
      console.error('Error creating knowledge base category:', error);
      toast({
        title: 'Error',
        description: 'Failed to create category',
        variant: 'destructive',
      });
      return null;
    }
  }, [categories.length, toast, user]);

  const moveKnowledgeBasesToCategory = useCallback(async (knowledgeBaseIds: string[], categoryId: string | null) => {
    if (!user || knowledgeBaseIds.length === 0) return false;

    try {
      if (categoryId === null) {
        const { error } = await supabase
          .from('knowledge_base_category_assignments')
          .delete()
          .eq('user_id', user.id)
          .in('knowledge_base_id', knowledgeBaseIds);

        if (error) throw error;

        setAssignments((current) => current.filter(
          (assignment) => !(assignment.user_id === user.id && knowledgeBaseIds.includes(assignment.knowledge_base_id))
        ));
        return true;
      }

      const nextAssignments = knowledgeBaseIds.map((knowledgeBaseId) => ({
        user_id: user.id,
        knowledge_base_id: knowledgeBaseId,
        category_id: categoryId,
      }));

      const { data, error } = await (supabase as any)
        .from('knowledge_base_category_assignments')
        .upsert(nextAssignments, { onConflict: 'user_id,knowledge_base_id' })
        .select('*');

      if (error) throw error;

      const updatedAssignments = (data ?? []) as KnowledgeBaseCategoryAssignment[];
      setAssignments((current) => {
        const remaining = current.filter(
          (assignment) => !(assignment.user_id === user.id && knowledgeBaseIds.includes(assignment.knowledge_base_id))
        );
        return [...remaining, ...updatedAssignments];
      });

      return true;
    } catch (error) {
      console.error('Error moving knowledge bases to category:', error);
      toast({
        title: 'Error',
        description: 'Failed to move knowledge bases to the selected category',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, user]);

  const assignmentByKnowledgeBaseId = useMemo(() => assignments.reduce<Record<string, string>>((map, assignment) => {
    map[assignment.knowledge_base_id] = assignment.category_id;
    return map;
  }, {}), [assignments]);

  return {
    categories,
    assignments,
    assignmentByKnowledgeBaseId,
    loading,
    fetchKnowledgeBaseCategories,
    createCategory,
    moveKnowledgeBasesToCategory,
  };
}